import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { HorizontalBarChart } from '@fluentui/react-charting';
import { useStyles } from './styles';

const dataVizColors = {
  control: '#637CEF',
  conversion: '#CA5010',
  search: '#9373C0',
  visit: '#E3008C',
};

const getPercentageString = (value, i18n) => {
  if (value === 'N/A') {
    return '--';
  }
  const percentageValue = i18n.formatDecimalToFixed(value * 100, { digits: 2 });
  return i18n.getString(_TL_('{{value}}%'), {
    value: percentageValue,
  });
};

const CustomDataLabel = ({ chartData }) => {
  const i18n = useContext(I18nContext);
  const rawValue = chartData[0].horizontalBarChartdata.x;
  return getPercentageString(rawValue, i18n);
};

CustomDataLabel.propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.shape({
    horizontalBarChartdata: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }).isRequired,
  })).isRequired,
};

export const TreatmentControl = ({
  color,
  control,
  treatment,
}) => {
  const styles = useStyles();
  const i18n = useContext(I18nContext);
  const treatmentLabel = i18n.getString(_TL_('Treatment'));
  const controlLabel = i18n.getString(_TL_('Control'));
  const learnMore = i18n.getString(_TL_('Click to learn more about'));
  const maxValue = treatment > control ? treatment : control;

  const data = [
    {
      chartData: [
        {
          color,
          horizontalBarChartdata: { x: treatment, y: maxValue },
          legend: treatmentLabel,
          yAxisCalloutData: getPercentageString(treatment, i18n),
        },
      ],
      chartTitle: (
        <span className={styles.label}>
          <span>{treatmentLabel}</span>
          <HelpButton
            aria-label={`${learnMore} ${treatmentLabel}`}
            topic="pop_BA_Experiments_Treatment"
          />
        </span>
      ),
    },
    {
      chartData: [
        {
          color: dataVizColors.control,
          horizontalBarChartdata: { x: control, y: maxValue },
          legend: controlLabel,
          yAxisCalloutData: getPercentageString(control, i18n),
        },
      ],
      chartTitle: (
        <span className={styles.label}>
          <span>{controlLabel}</span>
          <HelpButton
            aria-label={`${learnMore} ${controlLabel}`}
            topic="pop_BA_Experiments_Control"
          />
        </span>
      ),
    },
  ];

  const getCustomDataLabel = useCallback(props => (
    <CustomDataLabel {...props} />
  ), []);

  return (
    <HorizontalBarChart
      barChartCustomData={getCustomDataLabel}
      data={data}
    />
  );
};

TreatmentControl.propTypes = {
  color: PropTypes.string.isRequired,
  control: PropTypes.number.isRequired,
  treatment: PropTypes.number.isRequired,
};
